import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConnectionService } from '../../../../../services/connection-service';
import { AppConfig } from '../../../../app.config';
import { consultationFieldsToSkip } from '../../../../../components/user-chat-view/user-chat-view-summary/summary-fields';

// Photo columns the chat summary hides but a doctor reading a quiz consultation needs. Kept out of the
// shared skip list so the summary is not affected.
const PHOTO_FIELDS: Array<string> = ['HairfallPhotoTop', 'HairfallPhoto'];

const TEMPORARY_ANSWER_FIELDS_TO_SKIP: Array<string> = ['ACL', 'updatedAt', 'expireOn', 'user', 'objectId', 'dbArchive'];

@Component({
    selector: 'consultationSession-Info', templateUrl: './consultation-info-popup.html',
    standalone: false
})
export class ConsultationSessionInfoPopup {
  consultationSession: any;
  detectedIssues: Array<any> = [];
  answers: Array<{ key: string, value: string }> = [];
  temporaryAnswers: Array<{ key: string, value: string, createdAt: string }> = [];
  loadingAnswers: boolean = true;

  constructor(private conn: ConnectionService,
    public appConfig: AppConfig,
    private dialogRef: MatDialogRef<ConsultationSessionInfoPopup>,
    @Inject(MAT_DIALOG_DATA) private data: any) {
  }

  async ngOnInit(): Promise<void> {
    this.consultationSession = this.data.consultationSession;
    // PrivateInstantCheckupDebug may carry no detectedIssues; unguarded read threw out of ngOnInit.
    const debugDetectedIssues = this.consultationSession.get('PrivateInstantCheckupDebug')?.detectedIssues || {};
    Object.keys(debugDetectedIssues).forEach((each: string) => {
      this.detectedIssues.push({ key: each, value: debugDetectedIssues[each] });
    });
    try {
      // The list query projects a handful of columns, so the object handed over carries no answers.
      const [consultationSession]: Array<any> = await this.conn.findConsultationSession({
        limit: 1,
        where: { objectId: this.consultationSession.id },
      });
      if (consultationSession) this.consultationSession = consultationSession;
      await this.loadAnswers();
    } finally {
      this.loadingAnswers = false;
    }
  }

  private async loadAnswers(): Promise<void> {
    const attributes = this.consultationSession.toJSON();
    consultationFieldsToSkip
      .filter((key: string) => !PHOTO_FIELDS.includes(key))
      .forEach((key: string) => delete attributes[key]);
    this.answers = Object.keys(attributes)
      .map((key: string) => ({ key, value: attributes[key] }))
      .filter((each: { value: any }) => (Array.isArray(each.value)
        ? each.value.length > 0
        : each.value !== null && each.value !== '' && typeof each.value !== 'object'))
      .map((each: { key: string, value: any }) => (
        { key: each.key, value: Array.isArray(each.value) ? each.value.join(', ') : `${each.value}` }));
    await this.loadTemporaryAnswers();
  }

  // A quiz consultation stores most answers on the session itself, but questions whose USER run mode
  // writes to TemporaryAnswer (FemalePregOrNewMom) only land there. The rows are per user and not per
  // consultation, so each answer carries the date it was given — the same treatment as the patient page.
  private async loadTemporaryAnswers(): Promise<void> {
    const user = this.consultationSession.get('user');
    if (!user) return;
    const rows = (await this.conn.findTemporaryAnswer({
      descending: 'expireOn',
      where: { user },
    })).map((each: any) => each.toJSON());
    const seen: { [key: string]: boolean } = {};
    rows.forEach((row_: { [key: string]: any }) => {
      const row = row_;
      const { createdAt }: { createdAt: string } = row as { createdAt: string };
      TEMPORARY_ANSWER_FIELDS_TO_SKIP.forEach((key: string) => delete row[key]);
      delete row.createdAt;
      Object.keys(row).forEach((key: string) => {
        if (seen[key]) return;
        seen[key] = true;
        this.temporaryAnswers.push({ createdAt, key, value: Array.isArray(row[key]) ? row[key].join(', ') : `${row[key]}` });
      });
    });
  }

  hideModal(): void {
    this.dialogRef.close();
  }
}
